import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { OidcSecurityService } from 'angular-auth-oidc-client';
import { Observable, switchMap, take } from 'rxjs';

import { FeatureFlagService } from '@common/service/feature-flag.service';
import { DomainQuery } from '@common/store/domain.store';
import { CORE_ROW_REVISION_CLASS_KEY, CORE_ROW_REVISION_KEY } from '@common/util/constants';
import { removeProperties } from '@common/util/util';

/* prevents infinite loop when oidcSecurityService is initialised.
 * oidcSecurityService is initialised when any of its methods are called.
 * it calls /api/domain which triggers the interceptor, which calls getAccessToken()
 * which triggers the interceptor again.
 */
const ignoreUrls = ['/api/auth'];
const passthroughPrefixes = ['/mock-api'];

export function requestTransformerInterceptor(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (isExternalRequest(request)) {
    return next(request);
  }

  if (passthroughPrefixes.some((prefix) => request.url.startsWith(prefix))) {
    return next(request);
  }

  if (ignoreUrls.some((url) => request.url.endsWith(url))) {
    return next(request);
  }

  const domainQuery = inject(DomainQuery);
  const featureFlag = inject(FeatureFlagService);

  const domainValue = domainQuery.getValue();
  const coreApiEndpoint = domainValue?.api?.core?.replace(/\/$/, '');
  const requestUrl = request.url.replace(/^\//, '');

  // Remove element_id from request body (applies to both auth paths).
  let body = request.body;
  if (body) {
    // AK-723: `__record_revision__` is server bookkeeping a core list read puts on every row.
    // It must never travel back out. Shallow by design — it is the last line of defence for the
    // common "payload is the row itself" shape; nested models are cleaned recursively upstream by
    // removeTrackingIds/EXCLUDED_PROPERTIES. Runs AFTER ApiService.resolveRequest has already
    // filed the validator, so stripping here cannot defeat seeding.
    body = removeProperties(body, ['element_id', CORE_ROW_REVISION_KEY, CORE_ROW_REVISION_CLASS_KEY]);
  }
  const url = coreApiEndpoint ? { url: `${coreApiEndpoint}/${requestUrl}` } : {};

  // AK-476 + AK-474 dual-mode (2026-06-26): cookie auth is a per-request opt-in.
  // When the migration toggle (the capability) is ON, the FE is a cookie client:
  // declare `X-Auth-Mode: cookie` on EVERY request and send `withCredentials`; do
  // NOT attach the OIDC Bearer header. The header is MANDATORY — a request that
  // carries the aiwb_session cookie but omits it falls through to JWT server-side
  // (per Ranjit, AK-474). So it must be sent even before a session exists (the
  // bootstrap GET /api/auth/session), which is why this is gated on the capability
  // alone, not on the current authenticated state. Toggle OFF (default) → the
  // Bearer path below is byte-for-byte today's behaviour.
  const cookieMode = featureFlag.cookieSessionMigrationEnabled;

  if (cookieMode) {
    // CSRF: D3's recommended outcome is Origin allow-list validation = no frontend
    // work, so no token header is attached. If D3 later lands on a double-submit
    // token instead, the CSRF header would attach here, e.g.
    //   .set('X-CSRF-Token', csrfToken)
    const cloned = request.clone({
      ...url,
      body,
      params: request.params,
      withCredentials: true,
      headers: request.headers.set('X-Auth-Mode', 'cookie'),
    });
    return next(cloned);
  }

  const oidcSecurityService = inject(OidcSecurityService);
  const accessToken$ = oidcSecurityService.getAccessToken().pipe(
    take(1), // Ensure the token observable completes
  );

  return accessToken$.pipe(
    switchMap((authToken) => {
      const headers = authToken ? request.headers.set('Authorization', `Bearer ${authToken}`) : request.headers;

      request = request.clone({
        ...url,
        body,
        params: request.params,
        headers,
      });

      return next(request);
    }),
  );
}

function isExternalRequest(request: HttpRequest<unknown>) {
  try {
    return request.url.startsWith('http');
  } catch {
    // Fallback in case window is not available (e.g. in tests)
    return false;
  }
}
